import { useCallback } from 'preact/hooks';
import { useHover } from '../hooks/UNSAFE_useHover';
import { EmbeddedIconButton } from '../PRIVATE_EmbeddedIconButton';
import { Close } from '../UNSAFE_RedwoodIcons/Close';
import { Flex } from '../UNSAFE_Flex';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { useTabBarContext } from './useTabBarContext';
import { BaseTabBarItem, BaseTabBarItemProps } from './BaseTabBarItem';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';

type RemovableTabBarItemProps<K extends string | number> = Omit<
  BaseTabBarItemProps<K>,
  'removeIcon'
>;

export function RemovableTabBarItem<K extends string | number>({
  itemKey,
  label,
  icon,
  badge,
  metadata,
  severity
}: RemovableTabBarItemProps<K>) {
  const { selection, currentKey, layout, onRemove } = useTabBarContext();
  const isSelected = selection === itemKey;
  const isCurrent = currentKey === itemKey;

  const handleRemove = useCallback(() => {
    onRemove?.({ value: itemKey as K });
  }, [onRemove, itemKey]);

  const { hoverProps, isHover } = useHover({});

  //instead of specifying flex: '1 0 auto' in cssProps var
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  //TODO: In addition to aria-label a genric instruction is required for keyboard deletion
  //common to al tabs,after feedback from Curt's investigations.
  return (
    <div {...hoverProps} style={flexDimensions}>
      <Flex>
        <BaseTabBarItem
          itemKey={itemKey}
          label={label}
          icon={icon}
          badge={badge}
          metadata={metadata}
          severity={severity}
          removeIcon={
            <span data-oj-tabbar-item-remove-icon="true">
              <EmbeddedIconButton
                aria-label={translations.tabBarNavigationList_removeCueText()}
                size="xs"
                onAction={handleRemove}>
                {(isHover || isSelected || isCurrent) && <Close />}
              </EmbeddedIconButton>
            </span>
          }
        />
      </Flex>
    </div>
  );
}
